/**
 * @file
 * The leaflet map for BasstrakkMap component.
 */
import { LatLngExpression } from 'leaflet';
import * as React from 'react';
import { Map, ZoomControl } from 'react-leaflet';

// Util
import { getFallbackBounds, getMapBounds } from '../processing';

// Components
import BasstrakkMapMarkers from './BasstrakkMapMarkers';

// Types
import { IMarker, LayerType } from '../types';
import MapBoxGLLayer from './MapBoxGLLayer';

interface IProps {
  // Default lat setting.
  lat: number;
  // Default lng setting.
  lng: number;
  // Current layer applied to map.
  layer: LayerType;
  // Data for map markers.
  markers: IMarker[];
  // Default zoom level.
  zoom: number;
}

class BasstrakkMapDisplay extends React.PureComponent<IProps> {
  // Default lat / lng array created from props and used as center point for map.
  private defaultLatLng = [this.props.lat, this.props.lng];

  // Use default lat / lng props to create default map bounds.
  private defaultMapBounds = getFallbackBounds(this.props.lat, this.props.lng);

  // Check if we have a single marker and position key.
  public hasMarkers = (markers: IMarker[]) => markers[0] && markers[0].position;

  public render() {
    const { layer, markers, zoom } = this.props;

    // If we have markers, set the single position bounds, otherwise use default.
    const defaultOrPositionBounds = this.hasMarkers(markers)
      ? // Get bounds for a single marker. position[0] = lat, position[1] = lng
        getFallbackBounds(markers[0].position[0], markers[0].position[1])
      : this.defaultMapBounds;

    // Use marker position if it exists, otherwise use default lat / lng values.
    const defaultLatLngOrPosition = this.hasMarkers(markers)
      ? markers[0].position
      : this.defaultLatLng;

    // New bounds created on every filter click. Get bounds for a collection of
    // markers, or use default or single marker position.
    const bounds =
      markers.length > 1 ? getMapBounds(markers) : defaultOrPositionBounds;

    // If we have multiple markers, set the map center using the bounds center,
    // otherwise use default lat/lng or a single marker position.
    const center =
      markers.length > 1
        ? getMapBounds(markers).getCenter()
        : defaultLatLngOrPosition;

    return (
      <div className="basstrakk-map" data-testid="map-display">
        <Map
          zoom={zoom}
          zoomControl={false}
          bounds={bounds}
          useFlyTo={true}
          center={center as LatLngExpression}
        >
          <MapBoxGLLayer
            accessToken="pk.eyJ1IjoianNleHRvbiIsImEiOiJMdTg3NjhBIn0.Hc87k8R_OYGr8KiRgYhQ4w"
            style="mapbox://styles/jsexton/ckhexfj7113yg19nufy59wj9u"
          />
          <ZoomControl position="bottomright" />
          {markers.length && (
            <BasstrakkMapMarkers markerData={markers} layer={layer} />
          )}
        </Map>
      </div>
    );
  }
}

export default BasstrakkMapDisplay;
